import type { EffectDefinition, EffectsProp } from 'remotion';

/** Private descriptor param: the aspect (width / height) of the frame the
 *  footage is cover-fitted into. Pixel-scaled definitions strip it before the
 *  inner effect runs. */
export const FRAME_ASPECT_PARAM = '__frameAspect';

const isFrameAspect = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** Width of the part of a `width`×`height` source that stays visible once it is
 *  cover-fitted into a frame of aspect `frameAspect`. Without a valid aspect,
 *  the whole source width (the pre-S12 behaviour). */
export function visibleFrameWidth(width: number, height: number, frameAspect: unknown): number {
  return isFrameAspect(frameAspect) ? Math.min(width, height * frameAspect) : width;
}

/** Splits the private aspect param off the params the inner effect receives. */
export function takeFrameAspect(params: unknown): { frameAspect: unknown; params: Record<string, unknown> } {
  const { [FRAME_ASPECT_PARAM]: frameAspect, ...rest } = params as Record<string, unknown>;
  return { frameAspect, params: rest };
}

/** Folds the aspect into the effect key: Remotion's effect memo compares keys,
 *  so a params change it cannot see would be ignored. Unchanged without one. */
export function keyWithFrameAspect<P>(calculateKey: (params: P) => string): (params: P) => string {
  return (params) => {
    const frameAspect = (params as Record<string, unknown> | undefined)?.[FRAME_ASPECT_PARAM];
    const key = calculateKey(params);
    return frameAspect === undefined ? key : `${key}-frame-aspect-${frameAspect}`;
  };
}

/** Pixel-sized params are authored for 1080 px of VISIBLE frame width. Effects
 *  run on the decoded source before the cover crop, so they scale by the width
 *  of the source part that stays visible (`visibleFrameWidth`): a 9:16 export
 *  of a 16:9 source sees only H·9/16 of its width. Preview (≤ 1280 px canvas)
 *  and export (720/1080) agree because both use the composition aspect. Create
 *  once at module level so definition identity stays stable. */
export function pixelScaled<P>(definition: EffectDefinition<P, unknown>, keys: ReadonlyArray<keyof P>): EffectDefinition<P, unknown> {
  return {
    ...definition,
    calculateKey: keyWithFrameAspect(definition.calculateKey),
    apply: (args) => {
      const { frameAspect, params } = takeFrameAspect(args.params);
      const factor = visibleFrameWidth(args.width, args.height, frameAspect) / 1080;
      for (const key of keys) {
        const v = params[key as string];
        if (typeof v === 'number') params[key as string] = v * factor;
      }
      return definition.apply({ ...args, params: params as P });
    },
  };
}

type Descriptor = EffectsProp[number];

/** Swaps in a frame-aspect-aware definition and attaches the aspect param. The
 *  effect key is recomputed from the new params; without an aspect, params and
 *  key are exactly what the factory produced. */
export function withFrameAspect(descriptor: Descriptor, definition: unknown, frameAspect?: number): Descriptor {
  const def = definition as EffectDefinition<unknown, unknown>;
  const params = isFrameAspect(frameAspect)
    ? { ...(descriptor.params as Record<string, unknown>), [FRAME_ASPECT_PARAM]: frameAspect }
    : descriptor.params;
  return { ...descriptor, definition: def, params, effectKey: def.calculateKey(params) } as Descriptor;
}
